/**
 * FX Preset Library — save/load named bundles of all 6 effect param groups
 * (eq, compressor, limiter, reverb, delay, autotune) for any channel.
 *
 * Storage: localStorage at `elpm-fx-presets`. Async-safe (multiple tabs can
 * each have their own session — last write wins). Built-in presets are
 * always available; user presets are mutable.
 *
 * Schema:
 *   { id: string, name: string, builtin: boolean, params: ChannelFxParams }
 *   ChannelFxParams = { eq, compressor, limiter, reverb, delay, autotune }
 */

const STORAGE_KEY = "elpm-fx-presets";

// Hand-tuned starter presets — these cover the most common "I want this
// channel to sound polished" use-cases. Users can clone + edit them.
export const BUILTIN_PRESETS = [
  {
    id: "builtin-vocal-polish",
    name: "Vocal Polish",
    builtin: true,
    params: {
      eq:         { low: 1, mid: 1.5, high: 3.5 },         // gentle air boost
      compressor: { threshold: -20, ratio: 3.5 },          // tame dynamics
      limiter:    { threshold: -3, ratio: 20 },
      reverb:     { send: 0.18 },                          // subtle plate
      delay:      { time: 0.38, feedback: 0.0 },           // dry
      autotune:   { depth: 4, rate: 5 },                   // light vibrato
    },
  },
  {
    id: "builtin-drum-bus",
    name: "Drum Bus",
    builtin: true,
    params: {
      eq:         { low: 3, mid: -1, high: 2 },            // punch + crisp
      compressor: { threshold: -16, ratio: 6 },            // glue
      limiter:    { threshold: -1, ratio: 20 },            // mild loud
      reverb:     { send: 0.0 },
      delay:      { time: 0.25, feedback: 0.0 },
      autotune:   { depth: 0, rate: 5 },
    },
  },
  {
    id: "builtin-wide-pad",
    name: "Wide Pad Reverb",
    builtin: true,
    params: {
      eq:         { low: -2, mid: 0, high: 2 },
      compressor: { threshold: -28, ratio: 2 },
      limiter:    { threshold: -6, ratio: 12 },
      reverb:     { send: 0.7 },                           // big wash
      delay:      { time: 0.52, feedback: 0.3 },           // long echoes
      autotune:   { depth: 8, rate: 2.5 },
    },
  },
  {
    id: "builtin-tape-saturation",
    name: "Tape Saturation",
    builtin: true,
    params: {
      eq:         { low: 2.5, mid: -3, high: -2 },         // warm + dark
      compressor: { threshold: -22, ratio: 4 },
      limiter:    { threshold: -2, ratio: 20 },
      reverb:     { send: 0.08 },
      delay:      { time: 0.18, feedback: 0.15 },
      autotune:   { depth: 0, rate: 5 },
    },
  },
  {
    id: "builtin-dub-delay",
    name: "Dub Delay",
    builtin: true,
    params: {
      eq:         { low: 1, mid: 0, high: -2 },            // darker delay tail
      compressor: { threshold: -24, ratio: 3 },
      limiter:    { threshold: -3, ratio: 20 },
      reverb:     { send: 0.45 },
      delay:      { time: 0.66, feedback: 0.62 },          // self-oscillating echoes
      autotune:   { depth: 0, rate: 5 },
    },
  },
];

/** Read user-saved presets from localStorage. Returns [] on corruption. */
export function loadUserPresets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveUserPresets(presets) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
}

/** Add a new user preset. Returns the saved object (with id). */
export function addUserPreset(name, params) {
  const presets = loadUserPresets();
  const next = {
    id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: name.trim().slice(0, 40) || "Untitled preset",
    builtin: false,
    params: structuredClone(params),
    createdAt: new Date().toISOString(),
  };
  presets.unshift(next);
  saveUserPresets(presets);
  return next;
}

/** Delete a user preset by id. Built-ins are ignored. */
export function deleteUserPreset(id) {
  if (id?.startsWith("builtin-")) return false;
  const presets = loadUserPresets().filter((p) => p.id !== id);
  saveUserPresets(presets);
  return true;
}

/** Return the merged list (built-ins on top, user presets below). */
export function getAllPresets() {
  return [...BUILTIN_PRESETS, ...loadUserPresets()];
}

/** Look up a preset by id across built-ins + user-saved. */
export function findPreset(id) {
  return getAllPresets().find((p) => p.id === id) || null;
}
